export function hashString(input: string): number {
  let value = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    value ^= input.charCodeAt(index);
    value = Math.imul(value, 16777619);
  }
  return value >>> 0;
}

export function seeded(input: string, salt = 0): number {
  const value = hashString(`${input}:${salt}`);
  return (value % 10000) / 10000;
}

export function pick<T>(input: string, values: readonly T[], salt = 0): T {
  return values[Math.floor(seeded(input, salt) * values.length) % values.length];
}

function parseHex(color: string): [number, number, number] {
  const normalized = color.replace('#', '');
  if (normalized.length === 3) {
    return [
      parseInt(normalized[0] + normalized[0], 16),
      parseInt(normalized[1] + normalized[1], 16),
      parseInt(normalized[2] + normalized[2], 16),
    ];
  }
  return [parseInt(normalized.slice(0, 2), 16), parseInt(normalized.slice(2, 4), 16), parseInt(normalized.slice(4, 6), 16)];
}

function hex(value: number): string {
  return Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0');
}

export function mix(a: string, b: string, amount: number): string {
  const [ar, ag, ab] = parseHex(a);
  const [br, bg, bb] = parseHex(b);
  return `#${hex(ar + (br - ar) * amount)}${hex(ag + (bg - ag) * amount)}${hex(ab + (bb - ab) * amount)}`;
}

export function darken(color: string, amount = .28): string {
  return mix(color, '#151525', amount);
}

export function deepen(color: string, amount = .48): string {
  return mix(color, '#11111b', amount);
}

export function lighten(color: string, amount = .28): string {
  return mix(color, '#fff2c7', amount);
}

export function coolShadow(color: string, amount = .34): string {
  return mix(color, '#27304a', amount);
}

export function warmHighlight(color: string, amount = .32): string {
  return mix(color, '#ffe19a', amount);
}

export function alpha(color: string, opacity: number): string {
  const [r, g, b] = parseHex(color);
  return `rgba(${r},${g},${b},${opacity})`;
}

export function snap(value: number): number {
  return Math.round(value);
}

export interface PixelPalette {
  outline: string;
  shadow: string;
  base: string;
  highlight: string;
  accent: string;
  light: string;
}

export function palette(base: string, accent?: string): PixelPalette {
  const resolvedAccent = accent ?? warmHighlight(base, .42);
  return {
    outline: deepen(base, .72),
    shadow: coolShadow(base, .42),
    base,
    highlight: warmHighlight(base, .34),
    accent: resolvedAccent,
    light: lighten(resolvedAccent, .5),
  };
}

export function canvasRect(ctx: CanvasRenderingContext2D, color: string, x: number, y: number, width: number, height: number): void {
  ctx.fillStyle = color;
  ctx.fillRect(Math.round(x), Math.round(y), Math.round(width), Math.round(height));
}

export function canvasPixelLine(ctx: CanvasRenderingContext2D, color: string, points: Array<[number, number]>, size = 2): void {
  ctx.fillStyle = color;
  for (const [x, y] of points) ctx.fillRect(Math.round(x), Math.round(y), size, size);
}

export function contactShadow(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height = 5, opacity = .25): void {
  ctx.save();
  ctx.fillStyle = `rgba(10,12,15,${opacity})`;
  ctx.beginPath();
  ctx.ellipse(Math.round(x), Math.round(y), Math.round(width / 2), Math.round(height / 2), 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
